import Link from 'next/link';
import { memo, useCallback, useMemo, useState } from 'react';

import { useTrackPlanUpgradeConfirmed } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Modal, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { RadioGroup, RadioGroupItem } from '@air/primitive-radio-group';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { type PlanInfoType } from '~/components/Modals/PlansModal/utils/planInfo';
import { Routes } from '~/constants/routes';
import { useBillingCycle } from '~/hooks/useBillingCycle';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useUpdateSubscription } from '~/swr-hooks/subscriptions/useUpdateSubscription';
import { formatPrice } from '~/utils/currencyFormatters';
import { isBillingCycle, isSubscriptionOnTrial } from '~/utils/SubscriptionUtils';

export type PlanChangeConfirmationModalProps = Pick<
  PlanInfoType,
  'planId' | 'planTitle' | 'stripePlanIds' | 'prices' | 'pricingModel'
> & {
  action: Extract<PlanInfoType['action'], 'self-service-downgrade' | 'self-service-upgrade'>;
};

export const PlanChangeConfirmationModal = memo(
  ({
    action,
    planId,
    planTitle,
    stripePlanIds,
    prices,
    pricingModel,
    onClose,
  }: AirModalProps<PlanChangeConfirmationModalProps>) => {
    const { showToast } = useToasts();
    const [isLoading, setIsLoading] = useState(false);
    const [_, closePlansModal] = useAirModal(PlansModal);
    const { currentWorkspace } = useCurrentWorkspace();
    const { updateSubscription } = useUpdateSubscription();
    const [billingCycle, setBillingCycle] = useBillingCycle();
    const { data: subscription } = useSubscriptionContext();
    const isOnTrial = isSubscriptionOnTrial(subscription);
    const { trackPlanUpgradeConfirmed } = useTrackPlanUpgradeConfirmed();

    const { title, description, confirmButtonText, cancelButtonText, toastMessage } = useMemo(() => {
      const defaultPlanChangeDescription = `Your next invoice will be automatically prorated to reflect your workspace's new subscription.`;

      switch (action) {
        case 'self-service-downgrade': {
          if (isOnTrial) {
            return {
              cancelButtonText: 'Keep Trial',
              confirmButtonText: 'Cancel Trial',
              description: 'Your trial will be cancelled and you will immediately lose access to paid features.',
              title: 'Cancel trial?',
              toastMessage: 'Your trial has been cancelled',
            };
          } else {
            return {
              cancelButtonText: 'Keep Subscription',
              confirmButtonText: 'Change Plan',
              description: defaultPlanChangeDescription,
              title: `Downgrade to ${planTitle}?`,
              toastMessage: 'Successfully changed your plan',
            };
          }
        }
        case 'self-service-upgrade':
          return {
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Upgrade Plan',
            description: defaultPlanChangeDescription,
            title: `Upgrade to ${planTitle}?`,
            toastMessage: 'Your purchase was successful',
          };
      }
    }, [action, isOnTrial, planTitle]);

    const handlePlanChange = useCallback(async () => {
      const workspaceId = currentWorkspace?.id;
      const stripePlanId = stripePlanIds[billingCycle];

      setIsLoading(true);
      try {
        if (!workspaceId) {
          throw new Error('No workspace id');
        }

        if (!stripePlanId) {
          throw new Error('No stripe plan id found for selected plan/billing cycle');
        }

        await updateSubscription({ planId: stripePlanId, workspaceId });

        if (action === 'self-service-upgrade') {
          trackPlanUpgradeConfirmed({
            new_plan: planId,
            old_plan: subscription?.plan ?? 'basic',
            plan_type: billingCycle,
          });
        }

        closePlansModal();
        onClose();

        showToast(toastMessage);
      } catch (error) {
        reportErrorToBugsnag({
          context: 'Failed to update plan via plan change confirmation modal',
          error,
          metadata: {
            data: {
              action,
              planId,
              stripePlanId,
              workspaceId,
            },
          },
        });
        showToast('Failed to update plan. Please try again later.');
        setIsLoading(false);
      }
    }, [
      action,
      billingCycle,
      closePlansModal,
      currentWorkspace?.id,
      onClose,
      planId,
      showToast,
      stripePlanIds,
      subscription?.plan,
      toastMessage,
      trackPlanUpgradeConfirmed,
      updateSubscription,
    ]);

    const formattedPrices = {
      annual: prices.annual ? formatPrice(prices.annual) : 'Free',
      monthly: prices.monthly ? formatPrice(prices.monthly) : 'Free',
    };

    const onBillingCycleChange = useCallback(
      (value: string) => {
        if (isBillingCycle(value)) {
          setBillingCycle(value);
        }
      },
      [setBillingCycle],
    );

    const priceDetail = pricingModel === 'perMember' ? 'per member per month' : 'per month';

    return (
      <Modal dangerouslyBypassFocusLock data-testid="PLAN_CHANGE_CONFIRMATION_MODAL" isOpen onDismiss={onClose}>
        <header className="mb-4 flex flex-col gap-2">
          <ModalTitle>{title}</ModalTitle>
          <ModalDescription>{description}</ModalDescription>
        </header>

        <div className="mb-2 font-semibold ads:text-primary text-16 text-gray-12">Select your billing cycle</div>
        <RadioGroup onValueChange={onBillingCycleChange} value={billingCycle}>
          <RadioGroupItem align="center" id="monthly" value="monthly">
            <div className="font-medium ads:text-primary text-16 text-gray-12">Monthly</div>
            <div className="font-normal ads:text-metadata text-14 text-gray-10">
              {formattedPrices.monthly} {priceDetail} billed monthly
            </div>
          </RadioGroupItem>
          <RadioGroupItem align="center" id="annual" value="annual">
            <div className="font-medium ads:text-primary text-16 text-gray-12">Annual - Save 17%</div>
            <div className="font-normal ads:text-metadata text-14 text-gray-10">
              {formattedPrices.annual} {priceDetail} billed annually
            </div>
          </RadioGroupItem>
        </RadioGroup>

        <div className="mt-4 ads:text-secondary text-14 text-gray-11">
          By confirming your subscription, you allow Air to charge your card for this payment and future payments in
          accordance with their{' '}
          <Link href={Routes.marketing.terms} target="_blank">
            terms
          </Link>
          .
        </div>

        <footer className="mt-8 flex justify-end gap-2">
          <Button appearance="ghost" color="gray" onClick={onClose} size="large">
            {cancelButtonText}
          </Button>
          <Button
            appearance="filled"
            color="blue"
            data-testid="PLAN_CHANGE_CONFIRMATION_MODAL_BUTTON"
            isLoading={isLoading}
            onClick={handlePlanChange}
            size="large"
          >
            {confirmButtonText}
          </Button>
        </footer>
      </Modal>
    );
  },
);

PlanChangeConfirmationModal.displayName = 'PlanChangeConfirmationModal';
